import { useCallback, useEffect, useRef, useState } from 'react';

import MiaOrb from '@/components/MiaOrb';
import MiaSignature from '@/components/MiaSignature';
import avaFilm from '@/assets/images/memory-box/ava-film.jpg';
import film2 from '@/assets/images/memory-box/film-2.jpg';
import film3 from '@/assets/images/memory-box/film-3.jpg';
import familyPhoto from '@/assets/images/memory-box/family-1.jpg';

import './MemoryBox.css';

type Phase =
  | 'idle'
  | 'speak'
  | 'gather'
  | 'wrap'
  | 'turn'
  | 'promise'
  | 'untie'
  | 'unwrap'
  | 'reveal'
  | 'rest';

type MemoryBoxProps = {
  childName?: string;
};

const DURATION_SEC = 14;

/**
 * The stage the whole sequence is drawn against (GIFTWEB-10, from the
 * MiaMemoriesReveal design component).
 *
 * Every piece inside is positioned and sized in absolute pixels against these
 * numbers — `top-[100px]`, `right-[34px]`, the 224px box, the burst offsets,
 * the perimeter timer's viewBox. The card, though, is as wide as whatever
 * column it lands in: 346px on an iPhone, 516px in the desktop pane, 776px on
 * a tablet, where the mobile layout has no max width. Left alone, the pieces
 * stay pinned to their design coordinates while the frame stretches, so the
 * composition falls apart differently at every width (GIFTWEB-39 item 4).
 *
 * So the stage keeps these dimensions and is scaled to fit instead, the way
 * the video it imitates would be.
 */
const STAGE_WIDTH = 372;
const STAGE_HEIGHT = 228;

/** Half the timer stroke's width, so it sits just inside the card's border. */
const TIMER_INSET = 1.5;

const FILMS = [avaFilm, film2, film3];
const FILM_POSITIONS = ['50% 42%', '50% 32%', '50% 40%'];
const SENDERS: ReadonlyArray<[string, string, string, string]> = [
  ['Nan', 'N', '#f3e7c9', '#9c7a3c'],
  ['Uncle Steve', 'US', '#dbe7f0', '#3f6f96'],
  ['Aunty Nina', 'AN', '#f0dbe4', '#96416a'],
];

type BurstPiece = { style: React.CSSProperties; label: string };

function buildBurst(): BurstPiece[] {
  const avatars = [
    ['GA', '#f3e7c9', '#9c7a3c'],
    ['EM', '#dbe7f0', '#3f6f96'],
    ['AS', '#f0dbe4', '#96416a'],
    ['TR', '#dcefe6', '#256956'],
    ['JP', '#e9dcc4', '#8a5a2c'],
    ['MK', '#e7ded0', '#7a6a52'],
  ];
  const emojis = ['🎂', '💛', '📸', '🎈', '✨', '💌'];
  const confetti = ['#66bda4', '#93d5c9', '#256956', '#93d5c9', '#f8b0aa', '#9f1239'];
  const rnd = (a: number, b: number) => a + Math.random() * (b - a);

  return Array.from({ length: 26 }, (_, i) => {
    const ang = ((-90 + rnd(-80, 80)) * Math.PI) / 180; // launch into upper hemisphere
    const dist = rnd(85, 180);
    const ax = Math.round(Math.cos(ang) * dist);
    const ay = Math.round(Math.sin(ang) * dist); // negative = up
    const fx = Math.round(ax * rnd(1.3, 1.8) + rnd(-20, 20));
    const fy = Math.round(rnd(150, 280)); // falls below
    const r1 = Math.round(rnd(-120, 120));
    const r2 = Math.round(r1 + rnd(-360, 360));

    const style: React.CSSProperties = {
      position: 'absolute',
      top: '46%',
      left: '50%',
      ['--ax' as string]: `${ax}px`,
      ['--ay' as string]: `${ay}px`,
      ['--fx' as string]: `${fx}px`,
      ['--fy' as string]: `${fy}px`,
      ['--r1' as string]: `${r1}deg`,
      ['--r2' as string]: `${r2}deg`,
      animation: `mmMemBurst ${rnd(1.25, 1.9).toFixed(2)}s ${rnd(0, 0.18).toFixed(2)}s both`,
      zIndex: 12,
      willChange: 'transform,opacity',
    };

    const kind = i % 5;
    if (kind === 0 || kind === 1) {
      const [label, bg, fg] = avatars[i % avatars.length];
      const s = Math.round(rnd(26, 40));
      return {
        label,
        style: {
          ...style,
          width: s,
          height: s,
          borderRadius: '50%',
          aspectRatio: '1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: Math.round(s * 0.36),
          background: bg,
          color: fg,
          boxShadow: '0 5px 13px -5px rgba(15,41,34,.45)',
        },
      };
    }
    if (kind === 2) {
      const s = Math.round(rnd(23, 33));
      return {
        label: emojis[i % emojis.length],
        style: {
          ...style,
          width: s,
          height: s,
          borderRadius: '9px 9px 9px 3px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: Math.round(s * 0.5),
          background: '#fff',
          boxShadow: '0 5px 13px -5px rgba(15,41,34,.45)',
        },
      };
    }
    return {
      label: '',
      style: {
        ...style,
        width: Math.round(rnd(6, 11)),
        height: Math.round(rnd(8, 15)),
        borderRadius: 2,
        background: confetti[i % confetti.length],
      },
    };
  });
}

const PlayIcon = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='#fff' aria-hidden='true'>
    <path d='M8 5v14l11-7z' />
  </svg>
);

/**
 * The 14-second Mia Memory Box promo (GIFTWEB-10), ported from the
 * MiaMemoriesReveal design component. Self-contained CSS/DOM — no video.
 * Plays once when at least half of it scrolls into view; "Watch again"
 * replays. With reduced motion the rest state renders immediately.
 */
function MemoryBox({ childName }: MemoryBoxProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [filmScene, setFilmScene] = useState(0);
  const [remainingSec, setRemainingSec] = useState(DURATION_SEC);

  const stageRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);
  const timersRef = useRef<number[]>([]);
  const tickRef = useRef<number | null>(null);
  // Built once per play (not during render — buildBurst is random).
  const [burst, setBurst] = useState<BurstPiece[]>([]);

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
    if (tickRef.current !== null) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };

  const play = useCallback(() => {
    clearTimers();
    startedRef.current = true;
    setBurst(buildBurst());
    setPhase('speak');
    setFilmScene(0);
    setRemainingSec(DURATION_SEC);

    tickRef.current = window.setInterval(() => {
      setRemainingSec((s) => {
        if (s <= 1) {
          if (tickRef.current !== null) {
            clearInterval(tickRef.current);
            tickRef.current = null;
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    timersRef.current = [
      window.setTimeout(() => setPhase('gather'), 2200),
      window.setTimeout(() => setPhase('wrap'), 4800),
      window.setTimeout(() => setPhase('turn'), 6100),
      window.setTimeout(() => setPhase('promise'), 7500),
      window.setTimeout(() => setPhase('untie'), 9400),
      window.setTimeout(() => setPhase('unwrap'), 10300),
      window.setTimeout(() => {
        setPhase('reveal');
        setFilmScene(0);
      }, 10600),
      window.setTimeout(() => setFilmScene(1), 11850),
      window.setTimeout(() => setFilmScene(2), 13100),
      window.setTimeout(() => setPhase('rest'), 14400),
    ];
  }, []);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      startedRef.current = true;
      // Reduced motion: skip straight to the end state instead of animating.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPhase('rest');
      return;
    }

    // The desktop pane scrolls in an overflow container, not the viewport —
    // observe against the nearest scroll parent so the 50% threshold works there.
    let root: Element | null = null;
    let parent = el.parentElement;
    while (parent) {
      const overflowY = getComputedStyle(parent).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        root = parent;
        break;
      }
      parent = parent.parentElement;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            entry.intersectionRatio >= 0.5 &&
            !startedRef.current
          ) {
            play();
          }
        });
      },
      { root, threshold: [0, 0.5, 1] },
    );
    io.observe(el);

    return () => {
      io.disconnect();
      clearTimers();
    };
  }, [play]);

  /**
   * The card's inner box, so the stage can be scaled to fill it.
   *
   * `ResizeObserver` rather than a viewport listener: the desktop split layout
   * changes this pane's width without the window resizing. Re-rendering on a
   * resize is harmless — running CSS animations do not restart when React
   * updates an attribute, and the burst is held in state so it is not rebuilt.
   */
  const [cardBox, setCardBox] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width <= 0) return;
        // Only on a real change: the observer can deliver the same box more
        // than once, and a new object each time would re-render the component
        // mid-sequence for nothing.
        setCardBox((current) =>
          current.width === width && current.height === height
            ? current
            : { width, height },
        );
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = cardBox.width > 0 ? cardBox.width / STAGE_WIDTH : 1;

  const child = childName || '';
  const isSpeak = phase === 'speak';
  const isGather = phase === 'gather';
  const isWrap = phase === 'wrap';
  const isTurn = phase === 'turn';
  const isPromise = phase === 'promise';
  const isUntie = phase === 'untie';
  const isUnwrap = phase === 'unwrap';
  const isReveal = phase === 'reveal';
  const isRest = phase === 'rest';
  const isPoster = phase === 'idle';

  // `phase !== 'idle'` is exactly "has started", so no ref read is needed here.
  const isPlaying = !isPoster && !isRest;
  // Chip stays up for the whole sequence; only the rest screen drops it.
  const showDuration = !isRest;
  const clusterMounted = isGather || isWrap;
  const stageMounted = isWrap || isTurn || isPromise || isUntie || isUnwrap || isReveal;
  const boxMounted = isWrap || isTurn || isPromise || isUntie || isUnwrap;
  const lidsOpen = isUnwrap || isReveal;
  const ribbonMounted = isWrap || isTurn || isPromise || isUntie;
  const burstActive = isUnwrap || isReveal;

  const scene = Math.min(filmScene, 2);
  const [senderName, senderInitials, senderBg, senderFg] = SENDERS[scene];

  const boxRotation = isWrap
    ? 'rotateX(-52deg) rotateY(-16deg) scale(1)'
    : lidsOpen
      ? 'rotateX(-90deg) rotateY(0deg) scale(1.16)'
      : 'rotateX(-90deg) rotateY(0deg) scale(1)';

  const lidStyle = (side: 'left' | 'right'): React.CSSProperties => ({
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: '50%',
    overflow: 'hidden',
    transition: 'transform .6s cubic-bezier(.5,0,.3,1), opacity .5s ease',
    [side]: 0,
    background:
      side === 'left'
        ? 'linear-gradient(150deg,#2f7d68,#256956 70%,#0f2922)'
        : 'linear-gradient(210deg,#2f7d68,#256956 70%,#0f2922)',
    transform: lidsOpen
      ? `translateX(${side === 'left' ? '-103%' : '103%'})`
      : 'translateX(0)',
  });

  const ribbonBand: React.CSSProperties = {
    fill: 'none',
    stroke: '#93d5c9',
    strokeWidth: 17,
    strokeDasharray: 100,
    strokeDashoffset: 0,
  };

  const filmLayer = (i: number): React.CSSProperties => ({
    position: 'absolute',
    inset: -1,
    backgroundSize: 'cover',
    backgroundImage: `url('${FILMS[i]}')`,
    backgroundPosition: FILM_POSITIONS[i],
    opacity: scene === i ? 1 : 0,
    transition: 'opacity .5s ease',
  });

  const segStyle = (i: number): React.CSSProperties => ({
    height: '100%',
    borderRadius: 3,
    background: '#fff',
    ...(scene > i
      ? { width: '100%' }
      : scene === i
        ? { animation: 'mmProg 1.2s linear forwards' }
        : { width: 0 }),
  });

  const rimSide: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 224,
    height: 60,
    margin: '-30px 0 0 -112px',
  };
  const rimStripe = (
    <div className='absolute top-0 bottom-0 left-1/2 w-[18px] -translate-x-1/2 bg-[#93d5c9]' />
  );

  return (
    <div
      ref={stageRef}
      role='group'
      aria-label='Mia Memory Box'
      className='memory-box relative w-full overflow-hidden rounded-[18px] border border-[#e0e9e3] bg-[linear-gradient(158deg,#eef3ef_0%,#f8faf7_62%,#ffffff_100%)]'
    >
      {/*
        The design ratio lives on this frame rather than on the card, because
        `aspect-ratio` sizes the border box: on the card, the 1px border would
        be counted in, leaving the content box the stage actually fills at a
        slightly different ratio and the stage overflowing it by a fraction of
        a pixel. This frame has no border, so its box is exact and the card
        simply takes its height.
      */}
      <div
        ref={frameRef}
        data-testid='memory-box-frame'
        className='relative w-full aspect-372/228'
      >
      {/*
        Perimeter timer — depletes over the full sequence.

        Drawn outside the scaled stage, in the card's own pixels. It traces the
        card's border, so its corner radius has to stay at the card's 18px
        however the stage is scaled; inside the stage a fixed `rx` would be
        multiplied along with everything else and stop matching the corner
        (GIFTWEB-39). `pathLength` normalises the dash to 100 regardless of the
        rectangle's real size, so the depletion timing is unaffected.
      */}
      {isPlaying && cardBox.width > 0 && (
        <svg
          viewBox={`0 0 ${cardBox.width} ${cardBox.height}`}
          preserveAspectRatio='none'
          aria-hidden='true'
          data-testid='memory-box-timer'
          className='pointer-events-none absolute inset-0 z-20 h-full w-full'
        >
          {[
            { stroke: 'rgba(37,105,86,0.07)', animated: false },
            { stroke: 'rgba(37,105,86,0.22)', animated: true },
          ].map(({ stroke, animated }) => (
            <rect
              key={stroke}
              x={TIMER_INSET}
              y={TIMER_INSET}
              width={Math.max(0, cardBox.width - TIMER_INSET * 2)}
              height={Math.max(0, cardBox.height - TIMER_INSET * 2)}
              // The card's inner radius (18px less its 1px border), taken in
              // to the stroke's centreline.
              rx={17 - TIMER_INSET}
              pathLength={animated ? 100 : undefined}
              fill='none'
              stroke={stroke}
              strokeWidth='1.5'
              strokeLinecap={animated ? 'round' : undefined}
              vectorEffect='non-scaling-stroke'
              style={
                animated
                  ? { strokeDasharray: 100, animation: 'mmPerim 15.9s linear forwards' }
                  : undefined
              }
            />
          ))}
        </svg>
      )}

      {/* The design stage, scaled as one piece — see STAGE_WIDTH. */}
      <div
        data-testid='memory-box-stage'
        className='absolute left-0 top-0'
        style={{
          width: STAGE_WIDTH,
          height: STAGE_HEIGHT,
          /**
           * `zoom`, not `transform: scale()`. The box sequence is a 3-D scene:
           * a `perspective` container whose own transform is animated by
           * mmBoxAppear. Nesting that inside a scaled ancestor left Chrome
           * rasterising the subtree inconsistently — adjacent frames drew the
           * box either correctly or hugely magnified, which read as a flicker
           * through the wrap and turn phases. `zoom` scales layout instead, so
           * no second transform enters the chain and lengths like the 850px
           * perspective scale with everything else (GIFTWEB-39).
           */
          zoom: scale,
        }}
      >

      {/* orb during the opening line */}
      {isSpeak && (
        <div className='absolute left-1/2 top-[26px] z-[3] size-14 -translate-x-1/2 overflow-hidden rounded-full'>
          <MiaOrb size={56} mood='idle' />
        </div>
      )}

      {/* duration chip */}
      {showDuration && (
        <div
          aria-hidden='true'
          className='absolute right-[15px] top-3.5 z-[22] inline-flex items-center gap-[5px] rounded-[20px] bg-[rgba(37,105,86,0.1)] px-[9px] py-1 text-[#256956] animate-[mmRise_.4s_ease_both]'
          data-testid='memory-box-duration'
        >
          <svg
            width='11'
            height='11'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.4'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
          >
            <circle cx='12' cy='12' r='9' />
            <path d='M12 7v5l3 2' />
          </svg>
          <span className='text-[11px] font-bold tracking-[0.02em]'>
            0:{String(isPoster ? DURATION_SEC : remainingSec).padStart(2, '0')}
          </span>
        </div>
      )}

      {/* poster */}
      {isPoster && (
        <div className='absolute inset-0 flex flex-col items-center justify-center gap-3 px-6'>
          <div className='size-14 overflow-hidden rounded-full'>
            <MiaOrb size={56} mood='idle' />
          </div>
          <div className='text-center text-[15.5px] font-bold leading-[1.35] tracking-[-0.01em] text-[#0f2922]'>
            Send more than <span className='text-[#256956]'>just money</span>
          </div>
          <div className='inline-flex items-center gap-[7px] rounded-[22px] bg-[#256956] py-[7px] pl-[13px] pr-[15px] shadow-[0_10px_22px_-12px_rgba(37,105,86,0.6)]'>
            <PlayIcon size={13} />
            <span className='text-[12.5px] font-bold text-white'>
              Watch ({DURATION_SEC}s)
            </span>
          </div>
        </div>
      )}

      {/* opening line */}
      {isSpeak && (
        <div
          aria-hidden='true'
          className='absolute left-6 right-6 top-[100px] z-[5] text-center animate-[mmPop_.5s_cubic-bezier(.2,.7,.3,1)_both]'
        >
          <span className='inline-block rounded-[16px_16px_16px_5px] border border-[#e3ece6] bg-white px-[17px] py-[13px] text-[16.5px] font-bold leading-[1.35] tracking-[-0.01em] text-[#0f2922] shadow-[0_10px_26px_-16px_rgba(15,41,34,0.5)]'>
            You know… you can send more
            <br />
            than <span className='text-[#256956]'>just money</span>.
          </span>
        </div>
      )}

      {/* persistent subline (speak → gather) */}
      {(isSpeak || isGather) && (
        <div
          aria-hidden='true'
          className={`absolute bottom-3.5 left-0 right-0 z-[6] text-center text-[12.5px] font-bold text-[#3f7d6b] ${
            isSpeak ? 'animate-[mmRise_.5s_ease_.35s_both]' : ''
          }`}
        >
          Notes, voice &amp; videos — sent with your gift
        </div>
      )}

      {/* gather cluster */}
      {clusterMounted && (
        <div
          aria-hidden='true'
          className={`absolute inset-0 z-[1] text-left ${
            isWrap ? 'animate-[mmGenie_.8s_cubic-bezier(.55,0,.7,.35)_both]' : ''
          }`}
        >
          <div
            className='absolute left-6 top-6 rounded-[13px_13px_13px_4px] bg-white px-3 py-[9px] text-[12.5px] leading-[1.4] text-[#0f2922] shadow-[0_10px_22px_-12px_rgba(15,41,34,0.42)] animate-[mmShuffle_.7s_cubic-bezier(.2,.7,.3,1.15)_0s_both]'
            style={{ ['--rot' as string]: '-5deg' }}
          >
            Happy birthday, gorgeous girl! 🎂
          </div>
          <div
            className='absolute right-5 top-[58px] flex items-center gap-2 rounded-xl bg-white px-3 py-2 shadow-[0_10px_22px_-12px_rgba(15,41,34,0.42)] animate-[mmShuffle_.7s_cubic-bezier(.2,.7,.3,1.15)_.45s_both]'
            style={{ ['--rot' as string]: '4deg' }}
          >
            <div className='flex size-6 shrink-0 items-center justify-center rounded-full bg-[#e9dcc4] text-[10px] font-bold text-[#9c7a3c]'>
              GA
            </div>
            <div>
              <div className='text-[9.5px] text-[#656e68]'>Gift from</div>
              <div className='text-[12.5px] font-bold text-[#0f2922]'>
                Grandpa · £20
              </div>
            </div>
          </div>
          <div
            className='absolute left-5 top-[100px] flex items-center gap-[9px] rounded-[13px] bg-white px-3 py-[9px] shadow-[0_10px_22px_-12px_rgba(15,41,34,0.42)] animate-[mmShuffle_.7s_cubic-bezier(.2,.7,.3,1.15)_.9s_both]'
            style={{ ['--rot' as string]: '-3deg' }}
          >
            <div className='flex size-[26px] shrink-0 items-center justify-center rounded-full bg-[#256956]'>
              <PlayIcon size={11} />
            </div>
            <div className='flex h-[15px] w-[74px] items-center gap-[2px]'>
              {[
                ['40%', '#256956', '-.05s'],
                ['80%', '#256956', '-.3s'],
                ['100%', '#3f8f78', '-.6s'],
                ['55%', '#3f8f78', '-.18s'],
                ['85%', '#7fb8a0', '-.5s'],
                ['45%', '#7fb8a0', '-.72s'],
                ['70%', '#cfe0d8', '-.24s'],
              ].map(([h, bg, delay], i) => (
                <div
                  key={i}
                  className='w-[2.5px] rounded-[2px]'
                  style={{
                    height: h,
                    background: bg,
                    animation: `mmWave .8s ease-in-out infinite ${delay}`,
                  }}
                />
              ))}
            </div>
            <span className='shrink-0 text-[10px] text-[#656e68]'>
              Auntie Em · 0:14
            </span>
          </div>
          <div
            className='absolute right-[34px] top-[138px] flex items-center gap-[9px] animate-[mmShuffle_.7s_cubic-bezier(.2,.7,.3,1.15)_1.35s_both]'
            style={{ ['--rot' as string]: '5deg' }}
          >
            <div
              className='relative h-11 w-[62px] shrink-0 overflow-hidden rounded-[11px] border-2 border-white shadow-[0_10px_22px_-12px_rgba(15,41,34,0.5)]'
              style={{
                backgroundImage: `url('${familyPhoto}')`,
                backgroundSize: 'cover',
                backgroundPosition: '50% 35%',
              }}
            >
              <div className='absolute inset-0 bg-[rgba(15,41,34,0.18)]' />
              <div className='absolute left-1/2 top-1/2 flex size-[22px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/50 bg-white/30 backdrop-blur-[3px]'>
                <PlayIcon size={9} />
              </div>
            </div>
            <div className='rounded-xl bg-white px-[11px] py-2 text-[11.5px] font-bold text-[#0f2922] shadow-[0_10px_22px_-12px_rgba(15,41,34,0.42)]'>
              Party video 🎬
            </div>
          </div>
        </div>
      )}

      {/* box + reveal stage */}
      {stageMounted && (
        <div aria-hidden='true' className='absolute inset-0'>
          {boxMounted && (
            <div
              className={`absolute inset-0 z-[2] flex items-center justify-center [perspective:850px] [perspective-origin:50%_44%] ${
                isWrap
                  ? 'animate-[mmBoxAppear_.65s_cubic-bezier(.2,.8,.3,1.05)_.34s_both]'
                  : isPromise
                    ? 'animate-[mmFloat_3.2s_ease-in-out_infinite]'
                    : ''
              }`}
            >
              <div
                className='relative h-11 w-[224px] [transform-style:preserve-3d]'
                style={{
                  transition: 'transform 1.3s cubic-bezier(.33,0,.2,1)',
                  transform: boxRotation,
                }}
              >
                {/* rim faces (solid box thickness) */}
                <div className='absolute inset-0 [transform-style:preserve-3d]'>
                  <div
                    className='absolute left-1/2 top-1/2 bg-[#0f2922]'
                    style={{
                      width: 224,
                      height: 150,
                      margin: '-75px 0 0 -112px',
                      transform: 'rotateX(-90deg) translateZ(30px)',
                    }}
                  />
                  <div
                    className='overflow-hidden'
                    style={{
                      ...rimSide,
                      transform: 'translateZ(75px)',
                      background: 'linear-gradient(180deg,#2f7d68,#256956)',
                    }}
                  >
                    {rimStripe}
                  </div>
                  <div
                    className='overflow-hidden'
                    style={{
                      ...rimSide,
                      transform: 'rotateY(180deg) translateZ(75px)',
                      background: 'linear-gradient(180deg,#256956,#0f2922)',
                    }}
                  >
                    {rimStripe}
                  </div>
                  <div
                    className='overflow-hidden'
                    style={{
                      ...rimSide,
                      width: 150,
                      margin: '-30px 0 0 -75px',
                      transform: 'rotateY(90deg) translateZ(112px)',
                      background: 'linear-gradient(180deg,#256956,#0f2922)',
                    }}
                  >
                    {rimStripe}
                  </div>
                  <div
                    className='overflow-hidden'
                    style={{
                      ...rimSide,
                      width: 150,
                      margin: '-30px 0 0 -75px',
                      transform: 'rotateY(-90deg) translateZ(112px)',
                      background: 'linear-gradient(180deg,#256956,#0f2922)',
                    }}
                  >
                    {rimStripe}
                  </div>
                </div>
                {/* top face = film frame */}
                <div
                  className='absolute left-1/2 top-1/2 overflow-hidden bg-[#0f2922]'
                  style={{
                    width: 224,
                    height: 150,
                    margin: '-75px 0 0 -112px',
                    transform: 'rotateX(90deg) translateZ(30px)',
                    boxShadow: '0 26px 48px -22px rgba(15,41,34,0.7)',
                    transition: 'border-radius .3s ease',
                    borderRadius: lidsOpen ? 0 : 3,
                  }}
                >
                  <div
                    className='absolute -inset-px bg-cover'
                    style={{
                      backgroundImage: `url('${avaFilm}')`,
                      backgroundPosition: '50% 42%',
                    }}
                  />
                  <div className='absolute inset-0 bg-[linear-gradient(180deg,rgba(15,41,34,0.05)_45%,rgba(15,41,34,0.62))]' />
                  <div style={lidStyle('left')} />
                  <div style={lidStyle('right')} />
                  {/* ribbon — unravels on untie */}
                  {ribbonMounted && (
                    <svg
                      viewBox='0 0 224 150'
                      preserveAspectRatio='none'
                      className='pointer-events-none absolute inset-0 z-[2] h-full w-full overflow-visible'
                    >
                      <path
                        pathLength='100'
                        d='M112 0 L112 150'
                        style={{
                          ...ribbonBand,
                          animation: isUntie ? 'mmTrace .6s ease-in .22s forwards' : undefined,
                        }}
                      />
                      <path
                        pathLength='100'
                        d='M0 75 L224 75'
                        style={{
                          ...ribbonBand,
                          animation: isUntie ? 'mmTrace .6s ease-in .3s forwards' : undefined,
                        }}
                      />
                      <path
                        pathLength='100'
                        d='M104 104 C100 88 106 80 112 75 C86 60 78 86 112 75 C146 60 138 86 112 75 C118 80 124 88 120 104'
                        style={{
                          fill: 'none',
                          stroke: '#93d5c9',
                          strokeWidth: 11,
                          strokeLinecap: 'round',
                          strokeLinejoin: 'round',
                          strokeDasharray: 100,
                          strokeDashoffset: 0,
                          animation: isUntie ? 'mmTrace .55s ease-in 0s forwards' : undefined,
                        }}
                      />
                      <circle
                        cx='112'
                        cy='75'
                        r='7'
                        style={{
                          fill: '#93d5c9',
                          animation: isUntie ? 'mmFade .3s ease .4s forwards' : undefined,
                        }}
                      />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* reel — flat card that pops out of the box */}
          {isReveal && (
            <div className='absolute left-1/2 top-1/2 z-[8] h-[150px] w-[228px] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[14px] shadow-[0_26px_50px_-22px_rgba(15,41,34,0.7)] animate-[mmReelIn_.45s_cubic-bezier(.2,.8,.3,1.05)_both]'>
              <div style={filmLayer(0)} />
              <div style={filmLayer(1)} />
              <div style={filmLayer(2)} />
              <div className='absolute inset-0 bg-[linear-gradient(180deg,rgba(15,41,34,0.06)_45%,rgba(15,41,34,0.62))]' />
              <div className='absolute left-3 right-3 top-2.5 flex gap-[5px]'>
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className='h-[3px] flex-1 overflow-hidden rounded-[3px] bg-white/35'
                  >
                    <div style={segStyle(i)} />
                  </div>
                ))}
              </div>
              <div className='absolute bottom-0 left-0 right-0 flex items-center gap-2.5 px-3.5 py-[13px]'>
                <div
                  className='flex size-9 shrink-0 items-center justify-center rounded-full border-2 border-white/90 text-[13px] font-bold [aspect-ratio:1] shadow-[0_4px_12px_-5px_rgba(0,0,0,0.5)]'
                  style={{ background: senderBg, color: senderFg }}
                >
                  {senderInitials}
                </div>
                <div className='flex-1'>
                  <div className='text-[11.5px] text-white/85 [text-shadow:0_1px_8px_rgba(0,0,0,0.5)]'>
                    A memory from
                  </div>
                  <div className='text-[15px] font-extrabold tracking-[-0.01em] text-white [text-shadow:0_1px_10px_rgba(0,0,0,0.5)]'>
                    {senderName}
                  </div>
                </div>
                <div className='flex size-[30px] shrink-0 items-center justify-center rounded-full border border-white/40 bg-white/20 backdrop-blur-[3px]'>
                  <PlayIcon size={12} />
                </div>
              </div>
            </div>
          )}

          {/* captions */}
          <div className='absolute bottom-[15px] left-5 right-5 z-[5] text-center'>
            {isTurn && (
              <div className='inline-block rounded-[20px] bg-white/90 px-[15px] py-[7px] text-[13.5px] font-extrabold tracking-[-0.01em] text-[#0f2922] shadow-[0_8px_20px_-12px_rgba(15,41,34,0.4)] animate-[mmRise_.5s_ease_both]'>
                They’re all wrapped up…
              </div>
            )}
            {isPromise && (
              <div className='inline-block rounded-[18px] bg-white/90 px-[15px] py-2 text-[13.5px] font-extrabold leading-[1.3] tracking-[-0.01em] text-[#0f2922] shadow-[0_8px_20px_-12px_rgba(15,41,34,0.4)] animate-[mmRise_.5s_ease_both]'>
                …and given to {child || 'them'}
                <br />
                on their 18th birthday 🎁
              </div>
            )}
          </div>

          {/* memories burst out like confetti */}
          {isUnwrap && (
            <div className='absolute left-1/2 top-1/2 z-[5] size-[170px] rounded-full bg-[radial-gradient(circle,rgba(159,224,198,0.85),transparent_66%)] animate-[mmBurst_1s_ease-out_both]' />
          )}
          {burstActive &&
            burst.map((piece, i) => (
              <div key={i} style={piece.style}>
                {piece.label}
              </div>
            ))}
        </div>
      )}

      {/* rest */}
      {isRest && (
        <>
          <div
            aria-hidden='true'
            className='pointer-events-none absolute inset-0 z-0 overflow-hidden animate-[mmRise_.6s_ease_both]'
          >
            <div className='absolute -top-[30%] -left-[14%] size-[180px] rounded-full blur-[38px] animate-[mmHbA_14s_ease-in-out_infinite,mmHueCool_16s_ease-in-out_infinite]' />
            <div className='absolute top-[8%] -right-[16%] size-[180px] rounded-full blur-[40px] animate-[mmHbB_17s_ease-in-out_infinite,mmHueCool_16s_ease-in-out_-6s_infinite]' />
            <div className='absolute -bottom-[34%] left-[26%] size-[200px] rounded-full blur-[42px] animate-[mmHbC_19s_ease-in-out_infinite,mmHueWarm_20s_ease-in-out_infinite]' />
          </div>
          <div className='absolute inset-0 z-[1] flex items-center gap-[13px] p-4 text-left animate-[mmRise_.5s_ease_both]'>
            <div className='size-11 shrink-0 overflow-hidden rounded-full'>
              <MiaOrb size={44} mood='success' />
            </div>
            <div className='min-w-0 flex-1'>
              <div className='flex h-[22px] items-center gap-1.5'>
                <MiaSignature height={20} color='#0f2922' />
                <span className='relative top-px text-[10.5px] font-extrabold uppercase tracking-[0.06em] text-[#256956]'>
                  Memory Box
                </span>
              </div>
              <div className='mt-0.5 text-[17px] font-extrabold leading-[1.2] tracking-[-0.01em] text-[#0f2922]'>
                Send more than money
              </div>
              <div className='mt-[5px] text-[12.5px] leading-[1.45] text-[#5a635e]'>
                Add a personal message, opened by {child ? `${child}'s` : 'their'}{' '}
                parent today – they could save it for {child || 'them'} to enjoy
                again when they turn 18.
              </div>
              <button
                type='button'
                onClick={play}
                className='mt-[11px] inline-flex cursor-pointer items-center gap-1.5 rounded-[20px] border-none bg-[#e7f0ec] px-[13px] py-[7px] text-xs font-bold text-[#256956]'
              >
                <svg
                  width='13'
                  height='13'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='#256956'
                  strokeWidth='2.2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <path d='M23 4v6h-6M1 20v-6h6' />
                  <path d='M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15' />
                </svg>
                Watch again
              </button>
            </div>
          </div>
        </>
      )}
      </div>
      </div>
    </div>
  );
}

export default MemoryBox;
